import BrokenImage from '@mui/icons-material/BrokenImage';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Skeleton,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconPdf } from '../../../../assets/icons';
import { AttachmentAsset } from '../../../../models/standard/AttachmentAsset';
import { getPrimaryDate } from '../../../../utils/attachmentUtils';
import { immersightBlue, immersightBlue75 } from '../../../../utils/colors';
import { PLATFORM_URL } from '../../../../utils/constants';

interface AttachmentPDFContentProps {
  data: AttachmentAsset;
  onDelete: () => void;
}

const ImageNotFoundPlaceholder = ({ height = 180 }: { height?: number | string }) => (
  <Box
    sx={{
      width: '100%',
      height: height,
      borderRadius: 3,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#f0f0f0',
    }}
  >
    <Skeleton variant="rectangular" width="100%" height="100%" animation={false} />
    <Box
      sx={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
      }}
    >
      <BrokenImage sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="caption" fontWeight="bold">
        Preview not found
      </Typography>
    </Box>
  </Box>
);

export const AttachmentPDFContent: React.FC<AttachmentPDFContentProps> = ({
  data,
  onDelete,
}) => {
  const intl = useIntl();
  const [thumbError, setThumbError] = useState(false);

  const getThumbUrl = () => (data.thumbnailUrl ? `${PLATFORM_URL}${data.thumbnailUrl}` : '');
  const getDownloadUrl = () => (data.downloadUrl ? `${PLATFORM_URL}${data.downloadUrl}` : '');

  // Action: Open PDF in new Tab
  const handleOpenPdf = () => {
    const url = getDownloadUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Helper to format date and decide label
  const renderDateInfo = () => {
    const { date, fromShooting } = getPrimaryDate(data);
    
    if (!date) return null;

    const dateStr = `${date.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })} ${date.toLocaleTimeString('de-DE', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })}`;

    const labelId = fromShooting 
      ? 'AttachmentDrawer.image.shootingDate' // "Aufnahmedatum:"
      : 'AttachmentDrawer.image.creationDate'; // "Erstelldatum:"
    
    const defaultMessage = fromShooting ? 'Aufnahmedatum:' : 'Erstelldatum:';

    return (
      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem', mb: 0.5 }}>
        {intl.formatMessage({ id: labelId, defaultMessage })} {dateStr}
      </Typography>
    );
  };

  return (
    <Box sx={{ borderRadius: 2, p: 0, backgroundColor: '#ffffff' }}>
      
      {/* Title Row - Uses PDF Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '50%',
            backgroundColor: immersightBlue,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& svg': {
              width: '26px',
              height: '26px',
            },
            '& svg path': {
              fill: '#ffffff !important',
            },
          }}
        >
          <IconPdf />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700, color: '#333', fontSize: '1.1rem', wordBreak: 'break-word',
          }}
        >
          {data.fileName || intl.formatMessage({ id: 'AttachmentDrawer.pdf.untitled', defaultMessage: 'PDF' })}
        </Typography>
      </Box>

      {/* Metadata */}
      <Box sx={{ mb: 2 }}>
        {/* Dynamic Date Label */}
        {renderDateInfo()}

        {data.description && (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
            {data.description}
          </Typography>
        )}
      </Box>

      {/* Thumbnail (Clicking it also opens the new tab) */}
      <Box sx={{ mb: 3, cursor: 'pointer' }} onClick={handleOpenPdf}>
        {thumbError || !data.thumbnailUrl ? (
          // If PDF has no thumb generated, shows placeholder
          <ImageNotFoundPlaceholder height={180} />
        ) : (
          <Box
            component="img"
            src={getThumbUrl()}
            alt={data.fileName}
            onError={() => setThumbError(true)}
            sx={{
              width: '100%', height: 180, objectFit: 'cover',
              borderRadius: 3, display: 'block',
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.9 },
            }}
          />
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenPdf}
          sx={{
            backgroundColor: immersightBlue,
            borderRadius: '18px',
            fontWeight: 'bold',
            padding: '8px 24px',
            boxShadow: 'none',
            textTransform: 'uppercase',
            '&:hover': { backgroundColor: immersightBlue75, boxShadow: 'none' },
          }}
        >
          <FormattedMessage id="AttachmentDrawer.openInNewTab" defaultMessage="In neuem Tab öffnen" />
        </Button>
        
        <Button
          variant="contained"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{
            backgroundColor: immersightBlue,
            borderRadius: '18px',
            fontWeight: 'bold',
            padding: '8px 24px',
            boxShadow: 'none',
            textTransform: 'uppercase',
            '&:hover': { backgroundColor: immersightBlue75, boxShadow: 'none' },
          }}
        >
          <FormattedMessage id="AttachmentDrawer.deleteAttachment" defaultMessage="Attachment löschen" />
        </Button>
      </Box>
    </Box>
  );
};